import { useState } from "react";
import { AlertTriangle, PenLine, Loader2 } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

// Documento + autorizações facultativas independentes + assinatura. Usado no modo presencial e remoto.
export default function SignDocumentForm({ doc, onSubmit, identityRequired = false }) {
  const t = doc.template;
  const [choices, setChoices] = useState({});
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [idm, setIdm] = useState("");
  const [fields, setFields] = useState({});
  const decision = t.choices.filter((c) => c.group);
  const optional = t.choices.filter((c) => !c.group);
  const answered = optional.every((c) => !c.explicit || typeof choices[c.key] === "boolean");
  const ready = name.trim().length > 1 && sig && (!identityRequired || idm) && answered && (!decision.length || decision.some((c) => choices[c.key]));
  const pick = (key) => setChoices({ ...choices, ...Object.fromEntries(decision.map((c) => [c.key, c.key === key])) });

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try { await onSubmit({ signer_name: name.trim(), signature: sig, choices, fields, identity: identityRequired ? { method: idm } : undefined }); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="sign-document-form">
      {doc.demo_notice && (
        <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-200 text-xs p-3 flex gap-2" data-testid="sign-demo-notice">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {doc.demo_notice}
        </div>
      )}
      <div>
        <div className="font-display text-lg text-slate-50">{t.title}</div>
        <div className="text-xs text-slate-500 mt-1" data-testid="sign-document-meta">
          Versão {t.version} · Paciente: {doc.patient_name}{doc.practitioner_name ? ` · Osteopata: ${doc.practitioner_name}` : ""} · {new Date().toLocaleDateString("pt-PT")}
        </div>
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300 whitespace-pre-wrap max-h-[55vh] overflow-y-auto" data-testid="sign-document-body">
        {t.body}
      </div>
      {decision.length > 0 && (
        <div className="space-y-2" data-testid="sign-decision-group">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">A minha decisão (escolha uma opção)</div>
          {decision.map((c) => (
            <label key={c.key} className="flex items-start gap-3 rounded-xl border border-slate-800 p-3 text-sm text-slate-200 cursor-pointer">
              <input type="radio" name="sign-decision" data-testid={`sign-choice-${c.key}`} className="mt-0.5 w-4 h-4 accent-sky-500"
                checked={!!choices[c.key]} onChange={() => pick(c.key)} />
              {c.label}
            </label>
          ))}
        </div>
      )}
      {(t.fields || []).map((f) => (
        <div key={f.key} className="space-y-2">
          <label className="text-xs uppercase tracking-[0.2em] text-slate-500">{f.label}</label>
          <textarea data-testid={`sign-field-${f.key}`} rows={2} value={fields[f.key] || ""} maxLength={1000}
            onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
            className="w-full rounded-xl bg-slate-950 border border-slate-700 text-slate-100 px-3 py-2 text-sm" />
        </div>
      ))}
      {optional.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Autorizações facultativas (cada uma é independente)</div>
          {optional.map((c) => c.explicit ? (
            <div key={c.key} className="rounded-xl border border-slate-800 p-3 text-sm text-slate-200 space-y-2" data-testid={`sign-explicit-${c.key}`}>
              <div>{c.label}</div>
              <div className="grid grid-cols-2 gap-2">
                {[[true, "Autorizo"], [false, "Não autorizo"]].map(([v, l]) => (
                  <button key={l} type="button" onClick={() => setChoices({ ...choices, [c.key]: v })} aria-pressed={choices[c.key] === v}
                    data-testid={`sign-choice-${c.key}-${v ? "yes" : "no"}`}
                    className={`min-h-[44px] rounded-full border px-3 py-2 text-xs font-medium transition-colors ${choices[c.key] === v
                      ? (v ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300" : "border-rose-500/60 bg-rose-500/15 text-rose-300")
                      : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>{l}</button>
                ))}
              </div>
            </div>
          ) : (
            <label key={c.key} className="flex items-start gap-3 rounded-xl border border-slate-800 p-3 text-sm text-slate-200 cursor-pointer">
              <input type="checkbox" data-testid={`sign-choice-${c.key}`} className="mt-0.5 w-4 h-4 accent-sky-500"
                checked={!!choices[c.key]} onChange={(e) => setChoices({ ...choices, [c.key]: e.target.checked })} />
              {c.label}
            </label>
          ))}
          {t.footer && <p className="text-xs text-slate-400" data-testid="sign-document-footer">{t.footer}</p>}
        </div>
      )}
      <div className="space-y-2">
        <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Nome de quem assina</label>
        <input data-testid="sign-signer-name" value={name} onChange={(e) => setName(e.target.value)}
          className="w-full rounded-xl bg-slate-950 border border-slate-700 text-slate-100 px-3 py-2 text-sm" placeholder="Nome completo" />
      </div>
      {identityRequired && (
        <div className="space-y-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
          <label className="text-xs uppercase tracking-[0.2em] text-amber-300">Confirmação de identidade (pelo profissional)</label>
          <select data-testid="sign-identity-select" value={idm} onChange={(e) => setIdm(e.target.value)}
            className="w-full rounded-xl bg-slate-950 border border-slate-700 text-slate-100 px-3 py-2 text-sm">
            <option value="">Selecionar…</option>
            <option value="documento">Documento de identificação verificado</option>
            <option value="conhecido">Paciente conhecido do profissional</option>
          </select>
        </div>
      )}
      <SignaturePad onChange={setSig} />
      <p className="text-[11px] text-slate-500">{doc.ses_notice}</p>
      <button data-testid="sign-submit-btn" onClick={submit} disabled={!ready || busy}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-medium px-4 py-2.5 text-sm transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Assinar documento
      </button>
    </div>
  );
}
